import { Button } from "@cleanbudget/ui/components/button";
import { IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DetailFacts, DetailLayout, DetailSection } from "@/components/detail-layout";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { statusTone, useDescribe } from "./-components/describe";
import { DiscountCodeEditor, type DiscountCodeValues } from "./-components/discount-code-form";
import { OriginBadge } from "./-components/origin-badge";

const codeQuery = (id: string) => orpc.discountCode.get.queryOptions({ input: { id } });
const groupsQuery = () => orpc.price.listCustomerGroups.queryOptions();

export const Route = createFileRoute("/_admin/discount-code/$id")({
	loader: ({ context, params }) =>
		Promise.all([
			context.queryClient.ensureQueryData(codeQuery(params.id)),
			context.queryClient.ensureQueryData(groupsQuery()),
		]),
	component: EditDiscountCodePage,
});

/**
 * Edit a code. A save sends the version the editor loaded, so a code someone else saved meanwhile
 * is refused rather than overwritten. A code nobody used can be deleted; a used one stays for its
 * orders and is stopped with an end date.
 */
function EditDiscountCodePage() {
	const { t } = useTranslation("discountCode");
	const describe = useDescribe();
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { data: code, refetch } = useSuspenseQuery(codeQuery(id));
	const { data: settings } = useSuspenseQuery(groupsQuery());
	const [loads, setLoads] = useState(0);
	const update = useMutation({
		...orpc.discountCode.update.mutationOptions(),
		onSuccess: (saved) => {
			queryClient.setQueryData(codeQuery(id).queryKey, saved);
			return queryClient.invalidateQueries({ queryKey: orpc.discountCode.list.key() });
		},
	});
	const remove = useMutation({
		...orpc.discountCode.delete.mutationOptions(),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: orpc.discountCode.list.key(), refetchType: "all" }),
	});

	const onSubmit = async (values: DiscountCodeValues) => {
		await update.mutateAsync({ id, version: code.version, ...values });
	};

	return (
		<DetailLayout
			header={
				<PageHeader
					back={<BackLink to="/discount-code">{t("title")}</BackLink>}
					title={
						<span className="flex flex-wrap items-center gap-3">
							<span className="font-mono">{code.code}</span>
							<StatusBadge tone={statusTone(code.status)}>{describe.status(code.status)}</StatusBadge>
							<OriginBadge code={code} />
						</span>
					}
					description={describe.kind(code)}
					help="discountCode:edit"
				/>
			}
			aside={
				<DetailSection title={t("facts.title")} help="discountCode:overview">
					<div className="flex flex-col gap-4">
						<DetailFacts
							facts={[
								{ label: t("facts.status"), value: describe.status(code.status) },
								{ label: t("facts.uses"), value: describe.uses(code) },
								{ label: t("column.validity"), value: describe.validity(code) },
								{ label: t("facts.created"), value: formatDateTime(code.createdAt) },
								{ label: t("facts.updated"), value: formatDateTime(code.updatedAt) },
							]}
						/>
						{code.timesUsed > 0 ? (
							<p className="text-sm text-muted-foreground">{t("delete.used")}</p>
						) : (
							<ConfirmDialog
								trigger={
									<Button type="button" variant="destructive" className="w-fit">
										<IconTrash aria-hidden />
										{t("delete.button")}
									</Button>
								}
								title={t("delete.title")}
								description={t("delete.description", { code: code.code })}
								confirmLabel={t("delete.confirm")}
								onConfirm={async () => {
									await remove.mutateAsync({ id });
									await navigate({ to: "/discount-code" });
									queryClient.removeQueries({ queryKey: codeQuery(id).queryKey });
								}}
							/>
						)}
					</div>
				</DetailSection>
			}
		>
			{/* A new key after a reload starts the form again from what is stored now. */}
			<DiscountCodeEditor
				key={loads}
				code={code}
				customerGroups={settings.groups}
				submitLabel={t("edit.submit")}
				onSubmit={onSubmit}
				onReload={async () => {
					await refetch();
					setLoads((count) => count + 1);
				}}
			/>
		</DetailLayout>
	);
}
